// Frontend view of the backend country registry (GET /api/countries, backed by
// config_lookup rows `country_config:<ISO2>` in ooe-service — see
// ooe-service/utils/country-registry.js).
//
// Onboarding a country is a backend data change only. FALLBACK below mirrors the
// backend's built-in defaults and is used until the fetch completes (or if it
// fails), so the app behaves exactly as before when the endpoint is unreachable.
import {useEffect, useState} from 'react'

const intl = (iso, name) => ({
  iso,
  name,
  status: 'flag_gated',
  keapOnly: true,
  addressProvider: 'loqate',
})

const FALLBACK = [
  {iso: 'US', name: 'United States', status: 'enabled'},
  {iso: 'CA', name: 'Canada', status: 'enabled'},
  {iso: 'AU', name: 'Australia', status: 'enabled'},
  {iso: 'NZ', name: 'New Zealand', status: 'enabled'},
  {iso: 'GB', name: 'United Kingdom', status: 'enabled'},
  {iso: 'KY', name: 'Cayman Island', status: 'enabled', partnerAccess: false},
  {iso: 'BB', name: 'Barbados', status: 'enabled', partnerAccess: false},
  intl('ES', 'Spain'),
  intl('IT', 'Italy'),
  intl('IN', 'India'),
  intl('PH', 'Philippines'),
  intl('VN', 'Vietnam'),
  intl('GR', 'Greece'),
]

const normalize = c => ({
  ...c,
  iso: String(c.iso).toUpperCase(),
  name: c.name || c.iso,
  status: c.status || 'flag_gated',
  keapOnly: c.keapOnly === true,
  addressProvider: c.addressProvider === 'loqate' ? 'loqate' : 'google',
  partnerAccess: c.partnerAccess !== false,
})

let registry = FALLBACK.map(normalize)
const listeners = new Set()

export const getCountryRegistry = () => registry

export const getRegistryCountry = code =>
  registry.find(c => c.iso === String(code || '').toUpperCase())

export const setCountryRegistry = list => {
  registry = list
    .filter(c => c && c.iso && c.status !== 'disabled')
    .map(normalize)
  listeners.forEach(listener => listener())
}

export const subscribeToCountryRegistry = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// Fetches the registry once; silently keeps FALLBACK on any failure.
export async function loadCountryRegistry(client) {
  try {
    const response = await client('countries')
    if (Array.isArray(response?.countries) && response.countries.length) {
      setCountryRegistry(response.countries)
    }
  } catch (err) {
    console.error('Failed to fetch country registry, using defaults', err)
  }
}

// Re-renders the calling component when the registry loads. Returns the list.
export function useCountryRegistry() {
  const [, setVersion] = useState(0)
  useEffect(() => subscribeToCountryRegistry(() => setVersion(v => v + 1)), [])
  return registry
}

// Fully live: no feature flag needed to create accounts / purchase links.
export const isLiveCountry = code =>
  getRegistryCountry(code)?.status === 'enabled'

// Accepted but behind Enable_International_Account_Creation.
export const isFlagGatedCountry = code =>
  getRegistryCountry(code)?.status === 'flag_gated'

// Countries that already have a dedicated tax-number field (AU: ABN, NZ: NZBN) or are
// domestic (US, CA). Every other registry country is "international" and gets the
// generic optional Tax ID / VAT / GST field (BUSREQ-14451).
const NON_INTERNATIONAL_TAX_ID_COUNTRIES = ['US', 'CA', 'AU', 'NZ']

export const isInternationalTaxIdCountry = code => {
  const iso = String(code || '').toUpperCase()
  return (
    !!iso &&
    !NON_INTERNATIONAL_TAX_ID_COUNTRIES.includes(iso) &&
    !!getRegistryCountry(iso)
  )
}

export const isPartnerAccessCountry = code =>
  getRegistryCountry(code)?.partnerAccess === true
